import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { BusinessCostSummaryData } from 'pages/admin/CostDashboard/hooks/useGetAllBusinessSummaries';
import { useMemo } from 'react';
import TRow from 'shared/components/Table/TRow';
import { ColumnConfig } from 'shared/types/table';

import { COST_DASHBOARD_VARIANT } from '../../constants';

interface IBusinessResourceTable {
  businessCostSummaries: BusinessCostSummaryData[];
  variant: COST_DASHBOARD_VARIANT;
}

interface ResourceCountRow {
  businessKey: string;
  resourceCount: number;
}

const BusinessResourceTable: React.FC<IBusinessResourceTable> = ({
  businessCostSummaries,
  variant,
}) => {
  const resourceCountData = useMemo(() => {
    return businessCostSummaries
      .filter(entry => entry.data?.items?.length)
      .map(entry => ({
        businessKey: entry.businessKey,
        resourceCount: new Set(entry.data?.items.map(item => item.name)).size,
      }));
  }, [businessCostSummaries]);

  const columnConfig: ColumnConfig<ResourceCountRow>[] = useMemo(
    () => [
      {
        id: 'businessKey',
        Header: 'Name',
        Cell: row => <Typography sx={{ textTransform: 'uppercase' }}>{row.businessKey}</Typography>,
      },
      {
        id: 'resourceCount',
        Header: 'Resource Count',
        Cell: row => <Typography>{row.resourceCount}</Typography>,
        align: 'right',
      },
    ],
    [],
  );

  return (
    <Stack
      spacing={3}
      sx={{
        backgroundColor: '#fff',
        borderRadius: 2,
        boxShadow: 1,
        p: 3,
        width: '100%',
      }}
    >
      <Stack>
        <Typography variant="h6">
          {variant === COST_DASHBOARD_VARIANT.ROLLUP
            ? 'Business Resource Count'
            : 'Category Resource Count'}
        </Typography>

        <Typography sx={{ color: theme => theme.colors.functional.text.lint }} variant="subtitle1">
          The quantity of resources allocated to each business category.
        </Typography>
      </Stack>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columnConfig.map(col => (
                <TableCell align={col.align || 'left'} key={col.id} sx={{ width: col.width }}>
                  {col.Header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {resourceCountData.length > 0 ? (
              resourceCountData.map(row => (
                <TRow<ResourceCountRow>
                  columnConfig={columnConfig}
                  key={row.businessKey}
                  rowData={row}
                />
              ))
            ) : (
              <TableRow>
                <TableCell align="center" colSpan={columnConfig.length}>
                  <Typography sx={{ py: 2 }} variant="caption">
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default BusinessResourceTable;
